import React, { useEffect, useState } from 'react';
import './Absence.scss';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Table } from 'antd';
import {AbsenceStore} from '../../store/Absence';
import { EmptyState, Layout, UserPreview, Spinner } from '@iba-team/portal-react-components/dist';
import LeaveTypes from '../LeaveTypes';
import NoAbsencesImg from '../../assets/no-absences.svg';
import { useUpdateEffect } from 'react-use';
import { BREAKPOINT_BIG, BREAKPOINT_MD, BREAKPOINT_MLT_MD, BREAKPOINT_SM } from '../../consts';
import { RequestsStore } from '../../store/Requests';
import { WhoisoffVersionType } from '../../types';
import { format } from 'date-fns';
import { LeaveTypesStore } from '../../store/LeaveTypesList';
import { useDeviceSizeThreshold } from '../../utils/hooks';
import { PROFILE_ROUTE} from '../../utils/consts';
import { UserStore } from '../../store/User';
import { GlobalSearch } from '../../main-layout/header/GlobalSearch';
import apiConfig from '../../api/routes';
import { ProjectManagementStore } from '../../store/ProjectManagement';
import { buildModal } from '../../containers/MainWrapper/modal/ModalBuilder';
import { AbsenceSettingsModal, AbsenceSettingsModalI } from './modals/AbsenceSettingsModal';

const absenceSettingsModalCall =
  buildModal<undefined, AbsenceSettingsModalI>((currentItem, close) => <AbsenceSettingsModal {...currentItem} onClose={close}/>);

export const Absence = observer(() => {
  const {
    absenceList, getPortraitUrl, images,
    getAbsenceLists, absentEmployees, isFetchedAbsenceImages, isFetchedAbsenceList,
    whoIsOffVersion, setWhoIsOffVersion
  } = AbsenceStore;
  const [triggerUpdate, setTriggerUpdate] = useState<boolean>(false);
  const { requestsList } = RequestsStore;
  const {leaveTypesList} = LeaveTypesStore;
  const is576Screen = useDeviceSizeThreshold(BREAKPOINT_SM);
  const is670Screen = useDeviceSizeThreshold(BREAKPOINT_MLT_MD);
  const isTablet = useDeviceSizeThreshold(BREAKPOINT_MD);
  const isBigScreen = useDeviceSizeThreshold(BREAKPOINT_BIG);
  const { t } = useTranslation();
  const {profile} = UserStore;
  const {featureFlags, featureSelectors} = ProjectManagementStore;

  useEffect(() => {
    getAbsenceLists();
  }, [triggerUpdate]);

  useUpdateEffect(() => {
    getPortraitUrl();
  }, [absentEmployees]);


  useUpdateEffect(() => {
    let version: WhoisoffVersionType = 'VERSION_2';

    if (featureSelectors.SHOW_WHO_IS_OFF_VERSION_3_FOR_AMC.enabled && featureSelectors.SHOW_WHO_IS_OFF_VERSION_3_FOR_AML.enabled) {
      if (featureSelectors.SHOW_WHO_IS_OFF_VERSION_3_FOR_AMC.summaryValue || featureSelectors.SHOW_WHO_IS_OFF_VERSION_3_FOR_AML.summaryValue) {
        version = 'VERSION_3';
      } else {
        version = 'VERSION_2';
      }
    }
    else if (featureSelectors.SHOW_WHO_IS_OFF_VERSION_3_FOR_AMC.enabled) {
      version = featureSelectors.SHOW_WHO_IS_OFF_VERSION_3_FOR_AMC.summaryValue ? 'VERSION_3' : 'VERSION_2';
    } else if (featureSelectors.SHOW_WHO_IS_OFF_VERSION_3_FOR_AML.enabled) {
      version = featureSelectors.SHOW_WHO_IS_OFF_VERSION_3_FOR_AML.summaryValue ? 'VERSION_3' : 'VERSION_2';
    }  else {
      version = (profile.whoIsOffView !== 'NONE') ? profile.whoIsOffView : 'VERSION_2';
    }
    setWhoIsOffVersion(version);
  }, [featureSelectors, profile.whoIsOffView]);

  const findEqualUrl = (value: string)=> {
    const res = images.find(item=> item.originalUrl === value);
    return res?.blob;
  };

  const SCROLL_LIMIT = absenceList.leaveRequests.length < 4;

  const columns = [
    {
      title: `${t('employee')}`,
      dataIndex: 'employee',
      key: 'employee',
      width: is576Screen ? 130 : is670Screen ? 150 : isTablet ? 180 :  240,
      className: 'absence-table-cell'
    },
    {
      title: `${t('type')}`,
      dataIndex: 'type',
      key: 'type',
      width: is576Screen ? 40 : is670Screen ? 90 : isTablet ? 180 : undefined,
      className: 'absence-table-cell'
    },
    {
      title: `${t('fromTo')}`,
      dataIndex: 'fromTo',
      key: 'fromTo',
      width: isTablet ? 90 : undefined,
      className: 'absence-table-cell'
    }
  ];

  const showAbsenceSettingsModal = () => {
    absenceSettingsModalCall.showImmediately({
      onSave: () => {
        setTriggerUpdate(prev => !prev);
      }
    });
  };

  const getPreparedLeaveRequests = () => {
    let employees;
    if (!featureFlags.SHOW_BUSINESS_TRIPS) {
      employees = absentEmployees?.leaveRequests?.filter((i) => i.type !== 'BUSINESS_TRIP');
    } else {
      employees = absentEmployees?.leaveRequests;
    }

    return employees?.map(i => {
      const url = findEqualUrl(i.portraitUrl);

      return({
        key: i.id,
        employee:
          <UserPreview
            link= {{href: `${PROFILE_ROUTE}/${i.userId}`, target: '_blank'}}
            avatarLink={url}
            username={i.employeeName}
            showDefaultImage
            description={i.employeePosition}
            classes={{wrapperClassName: 'employeeInfoClass_Link'}}/>,
        type: <LeaveTypes leaveTypes={leaveTypesList} type={i.requestName} infoMessageVisible={true}/>,
        fromTo: <>{i.startDate && i.endDate && `${format(new Date(i.startDate), 'dd.MM.yyyy')}-${format(new Date(i.endDate), 'dd.MM.yyyy')}`}</>
      });
    });
  };

  const prepareLeaveRequests = getPreparedLeaveRequests();

  const prepareSpecialDays  = absentEmployees?.specialDays?.map(i => {
    const url = findEqualUrl(i.portraitUrl);

    return({
      key: i.id,
      employee: 
        <UserPreview 
          showDefaultImage
          avatarLink={url}
          username={i.employeeName} 
          description={i.employeePosition}
          link={{href: `${PROFILE_ROUTE}/${i.id}`, target: '_blank'}}
          classes={{wrapperClassName: 'employeeInfoClass_Link'}}/>,
      type: <div className={'country-code'}>
        <span>
          {i.countryCode}
        </span>
        <span style={{margin: '0 2px'}}>: </span>
        <span className={'reason'} style={{textTransform: 'capitalize'}}>
          {i.reason.toLowerCase()}
        </span>
      </div>,
      fromTo: <>{format(new Date(i.startDate), 'dd.MM.yyyy')}-{format(new Date(i.startDate), 'dd.MM.yyyy')}</>
    });
  });

  const mergeAbsence = () => {
    const isNonEmptyEmployees = absentEmployees?.absentEmployeesNumber > 0;
    const isNonEmptyArray = !!prepareSpecialDays;
    if(isNonEmptyEmployees && isNonEmptyArray){
      return [...prepareLeaveRequests, ...prepareSpecialDays];
    }
    if(isNonEmptyEmployees && !isNonEmptyArray){
      return [...prepareLeaveRequests];
    }
    if(isNonEmptyArray && !isNonEmptyEmployees){
      return [...prepareSpecialDays];
    }
    return [];
  };

  const renderHeaderByWhosOffVersion = () => {

    if(whoIsOffVersion !== 'VERSION_3') {
      return null;
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return <GlobalSearch widgetName={'WHO_IS_OFF'} className={'absence__global_search'} onClick={() => getAbsenceLists(undefined)}/>;
  };

  return <Layout header={{layoutName: <h3>{`${t('whoIsOff')} (${mergeAbsence().length})`}</h3>, 
    right: 
    <div className={'planner_header__inner'}>
      {renderHeaderByWhosOffVersion()}
    </div>}}
  className={ !requestsList.length || SCROLL_LIMIT ? 'absence absence_unset' : 'absence'}>
    {isFetchedAbsenceImages && isFetchedAbsenceList ?
      <>
        {mergeAbsence().length ?
          <Table
            className={'absence-list-table'}
            dataSource={mergeAbsence()}
            columns={columns}
            rowClassName={'absence-table-row-class'}
            pagination={false}
            scroll={isTablet ? { y: 200 } :requestsList.length && !isBigScreen ? { y: 290 } : requestsList.length && isBigScreen ? { y: 250 } : { y: 680 }}
          />
          :
          <EmptyState title={t('noAbsencesTitle')} description={t('noAbsencesDescr')} icon={<img src={NoAbsencesImg} alt='noAbsences'/>}/>
        }
        <div className={'absence_v3_footer'}>
          <span className={'absence_v3_footer__text'}>
            Based on my recent searches, project teams, division and custom lists.
          </span>
          <span className={'absence_v3_footer__link'}
            onClick={() => {
              apiConfig.statisticsUsageService.sendUsageRequest(apiConfig.statisticsUsageService.absenceSettingsFakeClick);
              showAbsenceSettingsModal();
            }}>Settings</span>
        </div>
      </>
      :
      <div className={'spinner_wrapper'}>
        <Spinner/>
      </div>
    }
  </Layout>;
});
